import React from 'react';

const iosSteps = [
  {
    step: 1,
    title: 'Open in Safari',
    description: 'Make sure you are using Safari browser. PWA installation is only available in Safari on iOS.',
    icon: (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
    )
  },
  {
    step: 2,
    title: 'Tap the Share button',
    description: 'Tap the Share icon at the bottom of the screen — it looks like a box with an arrow pointing up.',
    icon: (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
      </svg>
    )
  },
  {
    step: 3,
    title: 'Add to Home Screen',
    description: 'Scroll down in the share menu and tap "Add to Home Screen". Give it a name and tap "Add".',
    icon: (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <line x1="12" y1="8" x2="12" y2="16" />
        <line x1="8" y1="12" x2="16" y2="12" />
      </svg>
    )
  },
  {
    step: 4,
    title: 'Launch from Home Screen',
    description: 'Find the ConstructionPro icon on your home screen and tap it to open the app in full-screen mode.',
    icon: (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    )
  }
];

const androidSteps = [
  {
    step: 1,
    title: 'Open in Chrome',
    description: 'Make sure you are using Google Chrome browser for the best installation experience.',
    icon: (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
        <circle cx="12" cy="12" r="10" />
        <circle cx="12" cy="12" r="4" />
        <line x1="21.17" y1="8" x2="12" y2="8" />
        <line x1="3.95" y1="6.06" x2="8.54" y2="14" />
        <line x1="10.88" y1="21.94" x2="15.46" y2="14" />
      </svg>
    )
  },
  {
    step: 2,
    title: 'Open the menu',
    description: 'Tap the three-dot menu icon in the top-right corner of Chrome.',
    icon: (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
        <circle cx="12" cy="5" r="1" />
        <circle cx="12" cy="12" r="1" />
        <circle cx="12" cy="19" r="1" />
      </svg>
    )
  },
  {
    step: 3,
    title: 'Tap "Add to Home screen"',
    description: 'Select "Add to Home screen" or "Install app" from the dropdown menu.',
    icon: (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <line x1="12" y1="8" x2="12" y2="16" />
        <line x1="8" y1="12" x2="16" y2="12" />
      </svg>
    )
  },
  {
    step: 4,
    title: 'Confirm installation',
    description: 'Tap "Add" or "Install" in the confirmation dialog. The app icon will appear on your home screen.',
    icon: (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  }
];

const InstallSteps = ({ platform }) => {
  const steps = platform === 'android' ? androidSteps : iosSteps;
  const platformLabel = platform === 'android' ? 'Android (Chrome)' : 'iOS (Safari)';

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <h2 className="text-base font-semibold text-foreground mb-4">
        Installation Steps — {platformLabel}
      </h2>
      <div className="space-y-4">
        {steps?.map((item) => (
          <div key={item?.step} className="flex gap-4">
            <div className="flex-shrink-0 flex flex-col items-center">
              <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">
                {item?.step}
              </div>
              {item?.step < steps?.length && (
                <div className="w-px flex-1 bg-border mt-2" />
              )}
            </div>
            <div className="pb-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-primary">{item?.icon}</span>
                <h3 className="text-sm font-semibold text-foreground">{item?.title}</h3>
              </div>
              <p className="text-sm text-muted-foreground">{item?.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default InstallSteps;
